/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow

import type { Element } from 'react';

import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { Alert, Button, Input, Spinner } from 'reactstrap';
import { Virtuoso } from 'react-virtuoso';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
// eslint-disable-next-line import/extensions
import { fetchAuthSession } from 'aws-amplify/auth';
import config, { AGENT_CHAT_BASE_URL, API_BASE_URL } from '#support/app/config.js';
import {
  chatContainer,
  messageList,
  userMessageWrapper,
  assistantMessageWrapper,
  messageTime,
  userMessage,
  assistantMessage,
  messageContent,
  markdownContent,
  inputArea,
  inputWrapper,
  sendButton,
  errorAlert,
  loadingIndicator,
  typingDots,
  emptyState,
  fileAttachButton,
  filePreview,
  filePreviewName,
  fileRemoveButton,
  fileAttachmentIndicator,
  activityLog,
  activityItem,
  activityIcon,
  activityIconActive,
  activitySummary,
  progressLine,
  messageListFooter,
} from './AiAssistantSection.module.scss';

type UploadStatus = 'uploading' | 'done' | 'error';

type FileRef = {|
  name: string,
  fileKey: string,
  mediaType: string,
|};

type FileAttachment = {|
  id: string,
  name: string,
  mediaType: string,
  rawFile: File,
  uploadStatus: UploadStatus,
  fileRef: ?FileRef,
  uploadError: ?string,
|};

type Activity = {|
  id: number,
  label: string,
|};

type Message = {|
  role: 'user' | 'assistant',
  content: string,
  timestamp: string,
  fileNames?: Array<string>,
  activities?: Array<Activity>,
|};

// Parsing markdown is comparatively expensive. The chat re-renders on every
// state change — including unrelated ones like attachment upload progress — so
// without memoization each upload-status tick re-parses every prior assistant
// message. Memoizing on `content` makes those re-renders skip untouched
// messages, keeping the uploader (and the rest of the UI) responsive even with
// long conversations and large responses.
const REMARK_PLUGINS = [remarkGfm];

const MarkdownMessage = React.memo(({ content }: {| content: string |}) => (
  <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{content}</ReactMarkdown>
));

// Render the per-message activity log: the ordered list of steps the agent took
// (tool calls, attachment processing, knowledge base lookups). When `collapsed`
// the log is tucked behind a <details> summary; the live log is shown expanded.
// When `activeLast` the final step is still in progress and shows a spinner
// glyph; otherwise every step is marked done. Hoisted to module scope so it is a
// stable reference (lets the memoized message list skip rebuilds).
const renderActivityLog = (activities, { collapsed, activeLast }) => {
  if (!activities || activities.length === 0) return null;
  const lastIndex = activities.length - 1;
  const items = (
    <ul>
      {activities.map((act, i) => {
        const inProgress = activeLast && i === lastIndex;
        // Drop the trailing "..." from in-progress labels; the animated leading
        // glyph already conveys the ongoing state.
        const label = inProgress ? act.label.replace(/(\.{3}|…)\s*$/, '') : act.label;
        return (
          <li key={act.id} className={activityItem}>
            <span className={inProgress ? `${activityIcon} ${activityIconActive}` : activityIcon}>
              {inProgress ? '\u22EF' : '\u2713'}
            </span>
            <span>{label}</span>
          </li>
        );
      })}
    </ul>
  );
  if (!collapsed) return <div className={activityLog}>{items}</div>;
  const stepLabel = activities.length === 1 ? '1 step' : `${activities.length} steps`;
  return (
    <details className={activityLog}>
      <summary className={activitySummary}>{`Activity (${stepLabel})`}</summary>
      {items}
    </details>
  );
};

const formatMessageTime = (timestamp: string): string =>
  new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

// A single committed message. Memoized so that, within the virtualized list,
// only rows whose props actually change re-render.
const MessageRow = React.memo(
  ({ msg, prevMsg, isFirst }: {| msg: Message, prevMsg: ?Message, isFirst: boolean |}) => {
    const msgTime = new Date(msg.timestamp).getTime();
    const prevTime = prevMsg ? new Date(prevMsg.timestamp).getTime() : 0;
    const showTime = isFirst || msgTime - prevTime > 60000;

    return (
      <div className={msg.role === 'user' ? userMessageWrapper : assistantMessageWrapper}>
        {showTime && <div className={messageTime}>{formatMessageTime(msg.timestamp)}</div>}
        <div
          className={
            msg.role === 'user' ? userMessage : `${assistantMessage} ai-assistant-response`
          }
        >
          {msg.fileNames && msg.fileNames.length > 0 && (
            <div className={fileAttachmentIndicator}>
              {'\u{1F4CE}'} {msg.fileNames.join(', ')}
            </div>
          )}
          {msg.role === 'assistant' ? (
            <div className={markdownContent}>
              {renderActivityLog(msg.activities, { collapsed: true, activeLast: false })}
              <MarkdownMessage content={msg.content} />
            </div>
          ) : (
            <div className={messageContent}>{msg.content}</div>
          )}
        </div>
      </div>
    );
  }
);

type StreamingContext = {|
  isLoading: boolean,
  streamingActivities: Array<Activity>,
  streamingContent: string,
  streamingProgress: string,
|};

// Rendered inside the virtualized scroller, below the last committed message, as
// the Virtuoso Footer. Receiving the live streaming state via Virtuoso `context`
// lets the in-progress agent reply update without re-rendering the message list.
const StreamingFooter = ({ context }: {| context: StreamingContext |}) => {
  const { isLoading, streamingActivities, streamingContent, streamingProgress } = context;
  return (
    <>
      {isLoading && (
        <div className={assistantMessageWrapper}>
          <div className={assistantMessage}>
            {renderActivityLog(streamingActivities, {
              collapsed: false,
              activeLast: streamingContent.length === 0,
            })}
            {!streamingContent && streamingProgress && (
              <div className={progressLine}>
                <Spinner size="sm" />
                <span>{streamingProgress}</span>
              </div>
            )}
            {streamingContent ? (
              <div className={markdownContent}>
                <MarkdownMessage content={streamingContent} />
              </div>
            ) : (
              streamingActivities.length === 0 &&
              !streamingProgress && (
                <div className={loadingIndicator}>
                  <span className={typingDots}>
                    <span />
                    <span />
                    <span />
                  </span>
                  <span>Thinking</span>
                </div>
              )
            )}
          </div>
        </div>
      )}
      {/* Always-present spacer so the latest message never sits flush against the
          bottom border of the scroll area. */}
      <div className={messageListFooter} aria-hidden="true" />
    </>
  );
};

// Stable reference so Virtuoso doesn't treat the components map as new each render.
const VIRTUOSO_COMPONENTS = { Footer: StreamingFooter };

type Props = {|
  heading?: string,
  modelId: string,
  messages: Array<Message>,
  generation: ?number,
  uploadFile: (
    modelId: string,
    fileName: string,
    mediaType: string,
    fileSize: number
  ) => Promise<Object>,
  completeUpload: (
    modelId: string,
    fileKey: string,
    s3UploadId: string,
    parts: Array<{ partNumber: number, etag: string }>
  ) => Promise<Object>,
  addMessage: (modelId: string, message: Message) => void,
  clearHistory: (modelId: string, generation: ?number) => void,
  loadHistory: (modelId: string) => Promise<Object>,
  refreshModel: (modelId: string) => Promise<Object>,
|};

const AiAssistantSection = ({
  heading = 'SP3NCER',
  modelId,
  messages,
  generation,
  uploadFile,
  completeUpload,
  addMessage,
  clearHistory,
  loadHistory,
  refreshModel,
}: Props): Element<'div'> => {
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingActivities, setStreamingActivities] = useState<Array<Activity>>([]);
  const [streamingProgress, setStreamingProgress] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<Array<FileAttachment>>([]);
  const fileInputRef = useRef(null);
  // Maps file id → upload Promise<FileRef> so handleSend can await already-in-flight uploads
  const uploadPromisesRef = useRef<Map<string, Promise<FileRef>>>(new Map());
  // Bounds how many attachment uploads run at once. Without this, selecting many
  // files fires every initiate POST + S3 PUT in parallel, which overruns the
  // browser's per-host connection ceiling and API Gateway/Lambda concurrency —
  // surfacing as opaque "Failed to fetch" errors. Slots are handed off directly
  // from a finishing upload to the next waiter so active never exceeds the cap.
  const uploadSlotsRef = useRef<{ active: number, waiters: Array<() => void> }>({
    active: 0,
    waiters: [],
  });
  // Coalesces high-frequency streaming chunk renders into at most one per animation
  // frame. Re-rendering the full ReactMarkdown tree synchronously on every SSE token
  // (via flushSync) re-parses the entire accumulated response each time, which blocks
  // the main thread and locks the chat as the answer grows. Batching per frame keeps
  // the UI responsive while tokens still appear progressively. `latestStreamRef` holds
  // the newest text; `streamFrameRef` tracks the scheduled (or absent) frame.
  const streamFrameRef = useRef<?AnimationFrameID>(null);
  const latestStreamRef = useRef('');
  // Aborts the in-flight send (SSE stream included) when the user starts a new
  // chat mid-response, so the discarded reply never lands in the transcript.
  const abortControllerRef = useRef<?AbortController>(null);
  const [isResetting, setIsResetting] = useState(false);

  // The actual scroll DOM node behind Virtuoso, captured via `scrollerRef`. We
  // scroll this directly to its full bottom so the view clears the last message
  // plus the footer spacer (which `scrollToIndex` alignment would leave below).
  const scrollerElRef = useRef(null);
  // Whether the view is currently anchored at (or near) the bottom. Updated by
  // Virtuoso's `atBottomStateChange`. While true we keep following streamed
  // output; once the user scrolls up to read earlier content it flips false and
  // we stop yanking them back down.
  const atBottomRef = useRef(true);

  const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB (default)
  const TIFF_SIZE_LIMIT = 10 * 1024 * 1024; // 10MB
  const IFC_SIZE_LIMIT = 1024 * 1024 * 1024; // 1GB
  const E2K_SIZE_LIMIT = 100 * 1024 * 1024; // 100MB (ETABS .e2k text export)
  const UPLOAD_CONCURRENCY = 4; // max attachment uploads in flight at once
  const ACCEPTED_FILE_TYPES =
    '.pdf,.csv,.txt,.md,.html,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.tif,.tiff,.ifc,.e2k';

  // Load persisted chat history from the server on mount / when the model
  // changes, so the conversation is restored after a refresh or on return.
  useEffect(() => {
    loadHistory(modelId);
  }, [modelId, loadHistory]);

  // Pin the scroll node to its full height immediately (clearing the last
  // message AND the footer spacer). Used both for new messages and for live
  // streamed output.
  const pinToBottomNow = useCallback(() => {
    const el = scrollerElRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  // Auto-scroll to the very bottom whenever a message is appended. Virtuoso's
  // `followOutput` only tracks output while the user is already at the bottom;
  // this guarantees a scroll-to-bottom by default each time the message count
  // grows (user question or committed assistant reply). We pin the scroller node
  // to its full height across several animation frames: a single smooth scroll
  // gets interrupted (and stops short) as Virtuoso re-measures the newly mounted
  // message + its markdown, so instead we instantly re-pin to scrollHeight on
  // each frame until the layout settles, clearing the message AND footer spacer.
  useEffect(() => {
    if (messages.length === 0) return undefined;
    atBottomRef.current = true;
    let frame: ?AnimationFrameID = null;
    let tries = 0;
    const pin = () => {
      pinToBottomNow();
      tries += 1;
      if (tries < 6) frame = requestAnimationFrame(pin);
    };
    frame = requestAnimationFrame(pin);
    return () => {
      if (frame != null) cancelAnimationFrame(frame);
    };
  }, [messages.length, pinToBottomNow]);

  // Follow live streamed output. During streaming the message count does not
  // change (content grows inside the footer), so the effect above never fires;
  // and `followOutput` alone does not reliably keep pace as the markdown re-lays
  // out each frame. Re-pin to the bottom on every streaming update — but only
  // while the user is still anchored at the bottom, so scrolling up to read
  // earlier output is not constantly undone.
  useEffect(() => {
    if (!isLoading) return undefined;
    if (!atBottomRef.current) return undefined;
    const frame = requestAnimationFrame(pinToBottomNow);
    return () => cancelAnimationFrame(frame);
  }, [isLoading, streamingContent, streamingActivities, streamingProgress, pinToBottomNow]);

  const getMediaType = useCallback((fileName: string): string => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    const mediaTypes = {
      pdf: 'application/pdf',
      csv: 'text/csv',
      txt: 'text/plain',
      md: 'text/markdown',
      html: 'text/html',
      doc: 'application/msword',
      docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      xls: 'application/vnd.ms-excel',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      webp: 'image/webp',
      tif: 'image/tiff',
      tiff: 'image/tiff',
      ifc: 'application/ifc',
      e2k: 'application/octet-stream',
    };
    return mediaTypes[ext] || 'application/octet-stream';
  }, []);

  // Run an upload task once a concurrency slot is free, releasing the slot to
  // the next waiter when it settles (success or failure).
  const withUploadSlot = useCallback(async <T>(task: () => Promise<T>): Promise<T> => {
    const slots = uploadSlotsRef.current;
    if (slots.active >= UPLOAD_CONCURRENCY) {
      await new Promise<void>(resolve => {
        slots.waiters.push(resolve);
      });
    } else {
      slots.active += 1;
    }
    try {
      return await task();
    } finally {
      const next = slots.waiters.shift();
      if (next) {
        // Hand the slot directly to the next waiter; active count is unchanged.
        next();
      } else {
        slots.active -= 1;
      }
    }
  }, []);

  // Extracted so it can be called eagerly on file attach (not just at send time)
  const uploadOneFile = useCallback(
    async (fileToSend: FileAttachment): Promise<FileRef> => {
      // The FETCH_ACTION middleware rejects with an FSA action object on HTTP errors;
      // extract the readable message from the action's error payload
      const extractActionError = (actionErr, fallback) => {
        const payload = actionErr?.payload;
        const msg = payload instanceof Error ? payload.message : String(payload ?? fallback);
        return new Error(msg || fallback);
      };

      let uploadResponse;
      try {
        uploadResponse = await uploadFile(
          modelId,
          fileToSend.name,
          fileToSend.mediaType,
          fileToSend.rawFile.size
        );
      } catch (actionErr) {
        throw extractActionError(actionErr, 'Failed to initiate upload');
      }
      if (uploadResponse.error) {
        throw new Error(uploadResponse.payload?.message || 'Failed to get upload URL');
      }

      const { multipart, uploadUrl, fileKey, s3UploadId, parts, partSize } = uploadResponse.payload;

      if (multipart) {
        const file = fileToSend.rawFile;
        const completedParts = [];
        const concurrency = 3;
        const maxRetries = 3;

        const uploadPart = async (part, attempt = 0) => {
          const start = (part.partNumber - 1) * partSize;
          const end = Math.min(start + partSize, file.size);
          const blob = file.slice(start, end);
          try {
            const putRes = await fetch(part.uploadUrl, { method: 'PUT', body: blob });
            if (!putRes.ok) throw new Error(`HTTP ${putRes.status}`);
            return { partNumber: part.partNumber, etag: putRes.headers.get('ETag') };
          } catch (err) {
            if (attempt < maxRetries) {
              await new Promise(r => {
                setTimeout(r, 1000 * (attempt + 1));
              });
              return uploadPart(part, attempt + 1);
            }
            throw new Error(
              `Failed to upload part ${part.partNumber} after ${maxRetries + 1} attempts`
            );
          }
        };

        for (let i = 0; i < parts.length; i += concurrency) {
          const batch = parts.slice(i, i + concurrency);
          // eslint-disable-next-line no-await-in-loop
          const results = await Promise.all(batch.map(part => uploadPart(part)));
          completedParts.push(...results);
        }

        let completeResponse;
        try {
          completeResponse = await completeUpload(modelId, fileKey, s3UploadId, completedParts);
        } catch (actionErr) {
          throw extractActionError(actionErr, 'Failed to complete upload');
        }
        if (completeResponse.error) {
          throw new Error(completeResponse.payload?.message || 'Failed to complete upload');
        }
      } else {
        const maxPutRetries = 3;
        let lastPutErr;
        for (let attempt = 0; attempt <= maxPutRetries; attempt += 1) {
          try {
            // eslint-disable-next-line no-await-in-loop
            const putResponse = await fetch(uploadUrl, {
              method: 'PUT',
              headers: { 'Content-Type': fileToSend.mediaType },
              body: fileToSend.rawFile,
            });
            if (!putResponse.ok) {
              throw new Error(`Failed to upload file to storage (HTTP ${putResponse.status})`);
            }
            lastPutErr = null;
            break;
          } catch (err) {
            lastPutErr = err;
            if (attempt < maxPutRetries) {
              // eslint-disable-next-line no-await-in-loop
              await new Promise(r => {
                setTimeout(r, 1000 * (attempt + 1));
              });
            }
          }
        }
        if (lastPutErr) throw lastPutErr;
      }

      return { name: fileToSend.name, fileKey, mediaType: fileToSend.mediaType };
    },
    [uploadFile, completeUpload, modelId]
  );

  const handleFileSelect = useCallback(
    (e: SyntheticInputEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.target.files || []);
      if (selectedFiles.length === 0) return;

      const newFiles: Array<FileAttachment> = [];
      let validationError = null;

      for (let i = 0; i < selectedFiles.length; i += 1) {
        const file = selectedFiles[i];
        const ext = file.name.split('.').pop()?.toLowerCase();
        const isTiff = ext === 'tif' || ext === 'tiff';
        const isIfc = ext === 'ifc';
        const isE2k = ext === 'e2k';
        let sizeLimit = MAX_FILE_SIZE;
        let limitLabel = '500MB';
        if (isTiff) {
          sizeLimit = TIFF_SIZE_LIMIT;
          limitLabel = '10MB';
        } else if (isIfc) {
          sizeLimit = IFC_SIZE_LIMIT;
          limitLabel = '1GB';
        } else if (isE2k) {
          sizeLimit = E2K_SIZE_LIMIT;
          limitLabel = '100MB';
        }

        if (file.size > sizeLimit) {
          validationError = `"${file.name}" exceeds the ${limitLabel} limit for this file type. (${(file.size / (1024 * 1024)).toFixed(1)}MB)`;
          break;
        }
        // Use name+size+time as a stable id for this attachment session
        const id = `${file.name}-${file.size}-${Date.now()}`;
        newFiles.push({
          id,
          name: file.name,
          mediaType: getMediaType(file.name),
          rawFile: file,
          uploadStatus: 'uploading',
          fileRef: null,
          uploadError: null,
        });
      }

      if (validationError) {
        setError(validationError);
      } else {
        // Kick off uploads immediately — don't wait for the user to hit Send.
        // Each runs through withUploadSlot so no more than UPLOAD_CONCURRENCY
        // are in flight at once, even across multiple attach actions.
        newFiles.forEach(attachment => {
          const promise = withUploadSlot(() => uploadOneFile(attachment))
            .then(fileRef => {
              setAttachedFiles(prev =>
                prev.map(f =>
                  f.id === attachment.id ? { ...f, uploadStatus: 'done', fileRef } : f
                )
              );
              return fileRef;
            })
            .catch(err => {
              setAttachedFiles(prev =>
                prev.map(f =>
                  f.id === attachment.id
                    ? { ...f, uploadStatus: 'error', uploadError: err.message }
                    : f
                )
              );
              throw err;
            });
          uploadPromisesRef.current.set(attachment.id, promise);
        });
        setAttachedFiles(prev => [...prev, ...newFiles]);
        setError(null);
      }

      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [getMediaType, uploadOneFile, withUploadSlot]
  );

  const handleRemoveFile = useCallback((index: number) => {
    setAttachedFiles(prev => {
      const file = prev[index];
      if (file) uploadPromisesRef.current.delete(file.id);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleSend = useCallback(async () => {
    const hasMessage = inputText.trim();
    const hasFiles = attachedFiles.length > 0;
    if ((!hasMessage && !hasFiles) || isLoading || !modelId) return;

    const filesToSend = attachedFiles;

    let messageText;
    let userContent;
    if (hasMessage) {
      messageText = inputText.trim();
      userContent = inputText.trim();
    } else if (filesToSend.length === 1) {
      messageText = `Please summarize the attached document: ${filesToSend[0].name}`;
      userContent = `[Attached file: ${filesToSend[0].name}]`;
    } else {
      messageText = `Please summarize the ${filesToSend.length} attached documents`;
      userContent = `[Attached ${filesToSend.length} files: ${filesToSend.map(f => f.name).join(', ')}]`;
    }

    const userMsg = {
      role: 'user',
      content: userContent,
      timestamp: new Date().toISOString(),
      fileNames: filesToSend.map(f => f.name),
    };
    addMessage(modelId, userMsg);
    setInputText('');
    setAttachedFiles([]);
    setIsLoading(true);
    setError(null);
    setStreamingActivities([]);
    setStreamingProgress('');

    try {
      let fileRefs;
      if (filesToSend.length > 0) {
        // Await in-flight promises; retry any that failed during eager upload
        fileRefs = await Promise.all(
          filesToSend.map(f =>
            f.uploadStatus === 'error' ? uploadOneFile(f) : uploadPromisesRef.current.get(f.id)
          )
        );
        filesToSend.forEach(f => uploadPromisesRef.current.delete(f.id));
      }

      // Direct streaming fetch — bypasses Redux middleware to support SSE streaming
      const {
        tokens: { idToken },
      } = await fetchAuthSession();
      const agentChatBaseUrl = config(AGENT_CHAT_BASE_URL);
      const endpoint = agentChatBaseUrl
        ? `${agentChatBaseUrl}/model/${modelId}/agent/chat`
        : `/model/${modelId}/agent/chat`;

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const fetchResponse = await fetch(endpoint, {
        method: 'POST',
        mode: 'cors',
        signal: abortController.signal,
        headers: {
          Authorization: idToken.toString(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: messageText,
          displayContent: userContent,
          ...(generation != null ? { generation } : {}),
          ...(fileRefs && fileRefs.length > 0 ? { files: fileRefs } : {}),
        }),
      });

      if (fetchResponse.status === 409) {
        // The chat was reset (e.g. in another tab); resync to the new generation.
        loadHistory(modelId);
        throw new Error('This chat was reset elsewhere. The conversation has been reloaded.');
      }

      if (!fetchResponse.ok) {
        const errBody = await fetchResponse.json().catch(() => ({}));
        throw new Error(errBody.message || `HTTP ${fetchResponse.status}`);
      }

      const contentType = fetchResponse.headers.get('Content-Type') || '';
      if (contentType.includes('text/event-stream') && fetchResponse.body) {
        // SSE streaming: render tokens as they arrive
        const reader = fetchResponse.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let streamedText = '';
        // Local source of truth for activity entries so the completed message can
        // be tagged with them without depending on a flushed state read.
        const activities = [];

        // eslint-disable-next-line no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read();
          if (done) break;
          if (value) buffer += decoder.decode(value, { stream: true });

          // SSE events are separated by double newlines
          const parts = buffer.split('\n\n');
          buffer = parts.pop(); // keep incomplete trailing chunk

          for (let p = 0; p < parts.length; p += 1) {
            const part = parts[p];
            if (!part.startsWith('data: ')) continue; // eslint-disable-line no-continue
            let evt;
            try {
              evt = JSON.parse(part.slice(6));
            } catch {
              continue; // eslint-disable-line no-continue
            }
            if (evt.chunk) {
              streamedText += evt.chunk;
              // Coalesce renders: stash the newest text and schedule a single
              // render on the next animation frame instead of forcing a
              // synchronous full-markdown re-render per token (which locks the UI).
              latestStreamRef.current = streamedText;
              if (streamFrameRef.current == null) {
                streamFrameRef.current = requestAnimationFrame(() => {
                  streamFrameRef.current = null;
                  setStreamingContent(latestStreamRef.current);
                  // Answer tokens have begun; any transient progress counter is stale.
                  setStreamingProgress('');
                });
              }
            }
            if (evt.activity) {
              activities.push({ id: activities.length, label: evt.activity });
              const snapshot = activities.slice();
              flushSync(() => setStreamingActivities(snapshot));
            }
            if ('progress' in evt) {
              // Transient, self-updating counter (e.g. PDF page X of N). Not
              // persisted into the activity log; null/empty clears it.
              const progress = evt.progress || '';
              flushSync(() => setStreamingProgress(progress));
            }
            if (evt.error) throw new Error(evt.error);
          }
        }

        // Stream complete: drop any frame still queued so it can't fire a stray
        // render after the finished message is committed below.
        if (streamFrameRef.current != null) {
          cancelAnimationFrame(streamFrameRef.current);
          streamFrameRef.current = null;
        }

        const assistantMsg: Message =
          activities.length > 0
            ? {
                role: 'assistant',
                content: streamedText,
                timestamp: new Date().toISOString(),
                activities,
              }
            : {
                role: 'assistant',
                content: streamedText,
                timestamp: new Date().toISOString(),
              };
        addMessage(modelId, assistantMsg);
      } else {
        // Non-streaming fallback (e.g., API Gateway route without RESPONSE_STREAM)
        const data = await fetchResponse.json();
        addMessage(modelId, {
          role: 'assistant',
          content: data.completion,
          timestamp: new Date().toISOString(),
        });
      }

      // Refresh model data in the background so the form reinitializes with the
      // latest state. Fire-and-forget — don't block showing the assistant reply.
      refreshModel(modelId);
    } catch (err) {
      // A new-chat reset aborted this send on purpose — nothing to report, and
      // the partial reply must not be committed.
      if (err instanceof Error && err.name === 'AbortError') return;

      let errorMessage = 'An error occurred while communicating with the AI assistant';

      const msg = err instanceof Error ? err.message : String(err);

      if (msg && msg.includes('503')) {
        errorMessage = 'AI Assistant service is not yet configured.';
        errorMessage += ' Please contact your administrator.';
      } else if (msg && msg.includes('404')) {
        errorMessage = 'Model not found. Please refresh the page and try again.';
      } else if (msg && msg.includes('network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (msg && (msg.includes('not valid JSON') || msg.includes('Internal Server'))) {
        errorMessage =
          'The request timed out while processing your file. Try a smaller file or a message without an attachment.';
      } else if (msg) {
        errorMessage = msg;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
      // Cancel any queued streaming-render frame so it can't run after teardown.
      if (streamFrameRef.current != null) {
        cancelAnimationFrame(streamFrameRef.current);
        streamFrameRef.current = null;
      }
      setStreamingContent('');
      setStreamingActivities([]);
      setStreamingProgress('');
    }
  }, [
    inputText,
    attachedFiles,
    isLoading,
    generation,
    addMessage,
    loadHistory,
    refreshModel,
    modelId,
  ]);

  // Start a fresh conversation: archive the current one server-side (generation
  // bump), abort any in-flight reply so it is discarded, and clear the local
  // transcript. Uses a direct fetch because the Redux fetch middleware drops the
  // JSON Content-Type on DELETE. On a 409 (another tab already reset) or any
  // failure, resync from the server instead of guessing.
  const handleNewChat = useCallback(async () => {
    if (isResetting || !modelId) return;
    setIsResetting(true);
    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      const {
        tokens: { idToken },
      } = await fetchAuthSession();
      const response = await fetch(`${config(API_BASE_URL)}/model/${modelId}/agent/chat`, {
        method: 'DELETE',
        mode: 'cors',
        headers: {
          Authorization: idToken.toString(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(generation != null ? { generation } : {}),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        loadHistory(modelId);
        if (response.status !== 409) {
          setError(data.message || 'Failed to start a new chat. Please try again.');
          return;
        }
      }
      clearHistory(modelId, typeof data.generation === 'number' ? data.generation : null);
      setError(null);
    } catch (err) {
      loadHistory(modelId);
      setError('Failed to start a new chat. Please try again.');
    } finally {
      setIsResetting(false);
    }
  }, [isResetting, modelId, generation, clearHistory, loadHistory]);

  const handleKeyPress = useCallback(
    e => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  // Live streaming state handed to the virtualized list's Footer via Virtuoso
  // `context`, so the in-progress reply updates without re-rendering messages.
  const streamingContext = useMemo(
    () => ({ isLoading, streamingActivities, streamingContent, streamingProgress }),
    [isLoading, streamingActivities, streamingContent, streamingProgress]
  );

  // Renders one virtualized message row. Depends on `messages` only so the
  // closure stays stable across unrelated state changes (input, uploads, stream).
  const renderMessageItem = useCallback(
    (index: number, msg: Message) => (
      <MessageRow msg={msg} prevMsg={messages[index - 1]} isFirst={index === 0} />
    ),
    [messages]
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2>{heading}</h2>
        <Button
          id="aiAssistantNewChatButton"
          color="secondary"
          outline
          size="sm"
          onClick={handleNewChat}
          disabled={isResetting || (messages.length === 0 && !isLoading && !error)}
          title="Start a new conversation"
        >
          {isResetting ? <Spinner size="sm" /> : 'New chat'}
        </Button>
      </div>
      <div className={chatContainer}>
        {messages.length === 0 && !isLoading ? (
          <div className={messageList}>
            <div className={emptyState}>
              Ask a question about this model, SP3 methodology, or seismic performance assessment.
            </div>
          </div>
        ) : (
          // Virtualized so only on-screen messages are mounted. With persisted
          // history a conversation can grow large; rendering every message (and
          // its markdown) is what made the chat janky. `followOutput` keeps the
          // view pinned to the latest reply (and tracks the streaming Footer as
          // tokens arrive) when the user is already at the bottom.
          <Virtuoso
            scrollerRef={el => {
              scrollerElRef.current = el;
            }}
            className={messageList}
            data={messages}
            itemContent={renderMessageItem}
            context={streamingContext}
            components={VIRTUOSO_COMPONENTS}
            followOutput="auto"
            atBottomThreshold={120}
            atBottomStateChange={atBottom => {
              atBottomRef.current = atBottom;
            }}
            initialTopMostItemIndex={Math.max(0, messages.length - 1)}
            increaseViewportBy={600}
          />
        )}
        {error && (
          <Alert color="danger" className={errorAlert} toggle={() => setError(null)}>
            {error}{' '}
            <Button
              id="aiAssistantErrorNewChatButton"
              color="link"
              size="sm"
              className="p-0 align-baseline"
              onClick={handleNewChat}
              disabled={isResetting}
            >
              Start a new chat
            </Button>
          </Alert>
        )}
        <div id="aiAssistantInputArea" className={inputArea}>
          {attachedFiles.map((file, idx) => (
            <div key={file.id} className={filePreview}>
              <span className={filePreviewName}>
                {'\u{1F4CE}'} {file.name}
                {file.uploadStatus === 'uploading' && (
                  <Spinner size="sm" style={{ marginLeft: '4px', verticalAlign: 'middle' }} />
                )}
                {file.uploadStatus === 'done' && ' \u2713'}
                {file.uploadStatus === 'error' && ' \u26A0'}
              </span>
              <button
                type="button"
                className={fileRemoveButton}
                onClick={() => handleRemoveFile(idx)}
                aria-label={`Remove ${file.name}`}
              >
                {'\u2715'}
              </button>
            </div>
          ))}
          <div className={inputWrapper}>
            <input
              id="aiAssistantFileInput"
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept={ACCEPTED_FILE_TYPES}
              multiple
              style={{ display: 'none' }}
            />
            <Button
              className={fileAttachButton}
              color="secondary"
              outline
              onClick={() => fileInputRef.current?.click()}
              disabled={isLoading}
              size="sm"
              title="Attach a document"
            >
              {'\u{1F4CE}'}
            </Button>
            <Input
              id="aiAssistantMessageInput"
              type="textarea"
              value={inputText}
              onChange={e => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                attachedFiles.length > 0 ? 'Add a message or send to summarize' : 'Message'
              }
              rows="1"
              disabled={isLoading}
            />
            <Button
              id="aiAssistantSendButton"
              className={sendButton}
              color="primary"
              onClick={handleSend}
              disabled={isLoading || (!inputText.trim() && attachedFiles.length === 0)}
              size="sm"
            >
              Send
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiAssistantSection;
